import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import { observer } from 'mobx-react-lite';
import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import logo from '../../assets/brand/logo_site_onlight.svg';
import { messageContext } from './messageContext';

const LEVEL_ICON: Record<string, React.ReactNode> = {
  success: <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 26, flexShrink: 0 }} />,
  error:   <ErrorIcon   sx={{ color: '#f44336', fontSize: 26, flexShrink: 0 }} />,
  info:    <InfoIcon    sx={{ color: '#2196f3', fontSize: 26, flexShrink: 0 }} />,
  warning: <WarningIcon sx={{ color: '#ff9800', fontSize: 26, flexShrink: 0 }} />,
  loading: <CircularProgress size={22} thickness={4} sx={{ color: '#E61D50', flexShrink: 0 }} />,
};

const Message = () => {
    const messageStore = useContext(messageContext);
    const navigate = useNavigate();
    const { message, level, duration, link } = messageStore.getMessage() as { message: string; level: string; duration: number; link: { text: string; url: string; newTab?: boolean } | null };

    const handleClose = () => {
        messageStore.resetMessage();
    };

    return (
        <Snackbar
            open={message !== ''}
            autoHideDuration={duration}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
                    border: '1px solid rgba(0,0,0,0.06)',
                    px: 2,
                    py: 1.5,
                    minWidth: 260,
                    maxWidth: 400,
                }}
            >
                {/* Logo */}
                <img src={logo} alt="CherryClipz" style={{ height: 34, width: 'auto', flexShrink: 0 }} />

                {/* Message text + optional link */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#111827', lineHeight: 1.4 }}>
                        {message}
                    </Typography>
                    {link && (() => {
                        // Open in a new tab when the link asks for it (newTab) or it's external.
                        // Same-tab SPA navigation only for internal links that don't set newTab —
                        // this keeps the ET alive when you click "View in My Library" after export.
                        const openInNewTab = !!link.newTab || !link.url.startsWith('/');
                        return (
                        <a
                            href={link.url}
                            {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                            onClick={(e) => {
                                if (openInNewTab) {
                                    handleClose();
                                    return; // let the browser open the new tab
                                }
                                e.preventDefault();
                                handleClose();
                                navigate(link.url);
                            }}
                            style={{ fontSize: '12px', color: '#E61D50', fontWeight: 600, textDecoration: 'none', cursor: 'pointer' }}
                            onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                            onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}
                        >
                            {link.text} →
                        </a>
                        );
                    })()}
                </Box>

                {/* Level icon */}
                {LEVEL_ICON[level] ?? LEVEL_ICON.info}

                {/* Close */}
                <IconButton size="small" onClick={handleClose} sx={{ flexShrink: 0, ml: -0.5, p: '2px' }}>
                    <CloseIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                </IconButton>
            </Box>
        </Snackbar>
    );
};

export default observer(Message);
